import React, { useState, useCallback } from 'react';
import { Plus, Trash2, Save } from 'lucide-react';

interface ShapeItem {
  id: string;
  emoji: string;
  name: string;
  description: string;
  properties: string[];
}

interface ShapeRecognitionConfigurationFormProps {
  resource: any;
  onUpdate: (updates: Partial<any>) => void;
}

const ShapeRecognitionConfigurationForm: React.FC<ShapeRecognitionConfigurationFormProps> = ({
  resource,
  onUpdate
}) => {
  const [shapes, setShapes] = useState<ShapeItem[]>(
    resource.shapes || [
      {
        id: 'shape1',
        emoji: '🔴',
        name: 'Cercle',
        description: 'Une forme ronde sans angles',
        properties: ['Aucun côté droit', 'Entièrement courbe', 'Symétrique']
      }
    ]
  );

  const [instructions, setInstructions] = useState(
    resource.instructions || 'Reconnaissez et nommez les formes géométriques.'
  );

  const [showHints, setShowHints] = useState(resource.showHints ?? true);
  const [allowCorrections, setAllowCorrections] = useState(resource.allowCorrections ?? true);
  const [difficultyLevel, setDifficultyLevel] = useState(resource.difficultyLevel || 'easy');

  const addShape = useCallback(() => {
    const newShape: ShapeItem = {
      id: `shape${Date.now()}`,
      emoji: '🔷',
      name: 'Nouvelle forme',
      description: 'Description de la forme',
      properties: ['Propriété 1', 'Propriété 2']
    };
    setShapes(prev => [...prev, newShape]);
  }, []);

  const updateShape = useCallback((index: number, field: keyof ShapeItem, value: string) => {
    setShapes(prev => prev.map((shape, i) => 
      i === index ? { ...shape, [field]: value } : shape
    ));
  }, []);

  const updateShapeProperty = useCallback((index: number, propIndex: number, value: string) => {
    setShapes(prev => prev.map((shape, i) => 
      i === index 
        ? { 
            ...shape, 
            properties: shape.properties.map((prop, j) => 
              j === propIndex ? value : prop
            )
          }
        : shape
    ));
  }, []);

  const addShapeProperty = useCallback((index: number) => {
    setShapes(prev => prev.map((shape, i) => 
      i === index 
        ? { ...shape, properties: [...shape.properties, 'Nouvelle propriété'] }
        : shape
    ));
  }, []);

  const removeShapeProperty = useCallback((index: number, propIndex: number) => {
    setShapes(prev => prev.map((shape, i) => 
      i === index 
        ? { 
            ...shape, 
            properties: shape.properties.filter((_, j) => j !== propIndex)
          }
        : shape
    ));
  }, []);

  const removeShape = useCallback((index: number) => {
    if (shapes.length > 1) {
      setShapes(prev => prev.filter((_, i) => i !== index));
    }
  }, [shapes.length]);

  const handleSave = useCallback(() => {
    onUpdate({
      shapes,
      instructions,
      showHints,
      allowCorrections,
      difficultyLevel
    });
  }, [shapes, instructions, showHints, allowCorrections, difficultyLevel, onUpdate]);

  const commonShapes = [
    { emoji: '🔴', name: 'Cercle' },
    { emoji: '🔷', name: 'Carré' },
    { emoji: '🔺', name: 'Triangle' },
    { emoji: '📐', name: 'Rectangle' },
    { emoji: '🔶', name: 'Losange' },
    { emoji: '⭐', name: 'Étoile' },
    { emoji: '💠', name: 'Hexagone' },
    { emoji: '🔸', name: 'Pentagone' }
  ];

  return (
    <div className="space-y-6">
      {/* Instructions */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Instructions
        </label>
        <textarea
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows={3}
          placeholder="Instructions pour l'activité..."
        />
      </div>

      {/* Settings */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Niveau de difficulté
          </label>
          <select
            value={difficultyLevel}
            onChange={(e) => setDifficultyLevel(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="easy">Facile</option>
            <option value="medium">Moyen</option>
            <option value="hard">Difficile</option>
          </select>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="showHints"
            checked={showHints}
            onChange={(e) => setShowHints(e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="showHints" className="ml-2 block text-sm text-gray-700">
            Afficher les indices
          </label>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="allowCorrections"
            checked={allowCorrections}
            onChange={(e) => setAllowCorrections(e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="allowCorrections" className="ml-2 block text-sm text-gray-700">
            Permettre les corrections
          </label>
        </div>
      </div>

      {/* Shapes Configuration */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-800">Formes géométriques</h3>
          <button
            onClick={addShape}
            className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Ajouter une forme
          </button>
        </div>

        <div className="space-y-4">
          {shapes.map((shape, index) => (
            <div key={shape.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-700">Forme {index + 1}</h4>
                {shapes.length > 1 && (
                  <button
                    onClick={() => removeShape(index)}
                    className="text-red-600 hover:text-red-800 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Emoji */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Emoji
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={shape.emoji}
                      onChange={(e) => updateShape(index, 'emoji', e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="🔴"
                    />
                    <div className="flex flex-wrap gap-1">
                      {commonShapes.slice(0, 4).map((commonShape, i) => (
                        <button
                          key={i}
                          onClick={() => updateShape(index, 'emoji', commonShape.emoji)}
                          className="px-2 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
                          title={commonShape.name}
                        >
                          {commonShape.emoji}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nom de la forme
                  </label>
                  <input
                    type="text"
                    value={shape.name}
                    onChange={(e) => updateShape(index, 'name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Cercle"
                  />
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <input
                    type="text"
                    value={shape.description}
                    onChange={(e) => updateShape(index, 'description', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Description de la forme..."
                  />
                </div>

                {/* Properties */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Propriétés
                  </label>
                  <div className="space-y-2">
                    {shape.properties.map((property, propIndex) => (
                      <div key={propIndex} className="flex gap-2">
                        <input
                          type="text"
                          value={property}
                          onChange={(e) => updateShapeProperty(index, propIndex, e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Propriété de la forme..."
                        />
                        <button
                          onClick={() => removeShapeProperty(index, propIndex)}
                          className="px-2 py-2 text-red-600 hover:text-red-800 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => addShapeProperty(index)}
                      className="flex items-center gap-2 px-3 py-2 text-blue-600 hover:text-blue-800 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Ajouter une propriété
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
        >
          <Save className="w-4 h-4" />
          Sauvegarder la configuration
        </button>
      </div>
    </div>
  );
};

export { ShapeRecognitionConfigurationForm };
export default ShapeRecognitionConfigurationForm;
